/**
 * Where exported videos are written. Exports are streamed straight into a file (constant memory),
 * so a target hands out a writable file stream. Add targets (e.g. cloud upload) by implementing
 * `ExportTarget`.
 */
import { projectFolders } from '../projects/folders.ts';

export interface OpenedExportFile {
  /** Accepts positioned writes (`{ type: 'write', position, data }`), like FileSystemWritableFileStream. */
  writable: FileSystemWritableFileStream;
  /** Where the file ends up, for display (e.g. "Exports/My Film.mp4"). */
  label: string;
  /** Reads the finished file back (e.g. to play it). */
  getFile(): Promise<File>;
}

export interface ExportTarget {
  readonly id: string;
  readonly label: string;
  /**
   * Opens the destination file. May show UI (must then be called from a user gesture).
   * Resolves to null if the user cancelled.
   */
  open(projectId: string, fileName: string): Promise<OpenedExportFile | null>;
}

const EXPORTS_FOLDER = 'Exports';

function splitName(name: string): [string, string] {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? [name.slice(0, dot), name.slice(dot)] : [name, ''];
}

async function freeFileName(dir: FileSystemDirectoryHandle, name: string): Promise<string> {
  const [base, ext] = splitName(name);
  for (let i = 1; ; i++) {
    const candidate = i === 1 ? name : `${base} (${i})${ext}`;
    try {
      await dir.getFileHandle(candidate);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotFoundError') return candidate;
      throw err;
    }
  }
}

/** Writes into `Exports/` inside the project folder (no extra prompt). */
export const projectFolderTarget: ExportTarget = {
  id: 'project-folder',
  label: 'Project folder (Exports/)',
  async open(projectId, fileName) {
    const root = await projectFolders.get(projectId);
    if (!root) throw new Error('The project folder is not connected');
    const dir = await root.getDirectoryHandle(EXPORTS_FOLDER, { create: true });
    const name = await freeFileName(dir, fileName);
    const handle = await dir.getFileHandle(name, { create: true });
    return {
      writable: await handle.createWritable(),
      label: `${EXPORTS_FOLDER}/${name}`,
      getFile: () => handle.getFile(),
    };
  },
};

/** Lets the user choose any location ("Save as…"). */
export const saveAsTarget: ExportTarget = {
  id: 'save-as',
  label: 'Choose location…',
  async open(projectId, fileName) {
    let handle: FileSystemFileHandle;
    try {
      handle = await window.showSaveFilePicker({
        id: 'cutroom-export',
        suggestedName: fileName,
        startIn: (await projectFolders.get(projectId)) ?? 'videos',
        types: [{ description: 'MP4 video', accept: { 'video/mp4': ['.mp4'] } }],
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return null;
      throw err;
    }
    return { writable: await handle.createWritable(), label: handle.name, getFile: () => handle.getFile() };
  },
};

export const EXPORT_TARGETS: ExportTarget[] = [projectFolderTarget, saveAsTarget];
